import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../../../components/Layout';
import { apiFetch, apiOpenBlob } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import ApiInspector from '../../../components/ApiInspector';
import { API_DOCS } from './AssegnazioniDispositiviPage.api';
import './AssegnazioniDispositiviPage.css';

// ── Icone ────────────────────────────────────────────────────────────────────
const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const LinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);
const UploadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);
const SwapIcon = ({ size = 48 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);

// ── Tipi ─────────────────────────────────────────────────────────────────────
interface DipendenteOption { id: number; nome: string; cognome: string; mansione: string | null; }

interface DispositivoOption {
  id: number;
  codiceMatricola: string;
  tipologia: { id: number; nome: string };
  marca: string | null;
  modello: string | null;
  stato: string;
}

interface Assegnazione {
  id: number;
  dispositivo: { id: number; codiceMatricola: string; tipologia: { nome: string } };
  dipendente: { id: number; nome: string; cognome: string };
  consegnato: { id: number; nome: string; cognome: string } | null;
  dataAssegnazione: string;
  dataRestituzione: string | null;
  noteRestituzione: string | null;
  note: string | null;
  verbaleFileName: string | null;
}

interface AssegnazioneDipendente {
  id: number;
  dispositivo: { id: number; codiceMatricola: string; tipologia: { nome: string } };
  dipendente: { id: number; nome: string; cognome: string };
  dataAssegnazione: string;
  dataRestituzione: string | null;
  note: string | null;
}

interface RiepilogoDipendente {
  dipendente: { id: number; nome: string; cognome: string };
  assegnazioni: AssegnazioneDipendente[];
}

const fmtDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('it-IT') : '—';

export default function AssegnazioniDispositiviPage() {
  const { can } = usePermesso();
  const canEdit = can('aziendale.hr.assegnazioni_dispositivi', 'edit');

  const {
    societaId, setSocietaId, listaSocieta, isUnica, pronta,
  } = useFiltroSocieta();

  // Lista principale
  const [assegnazioni, setAssegnazioni] = useState<Assegnazione[]>([]);
  const [loading, setLoading] = useState(false);

  // Filtri
  const [fQ, setFQ] = useState('');
  const [fStato, setFStato] = useState('tutte');

  // Modal nuova/modifica assegnazione
  const [showModalForm, setShowModalForm] = useState(false);
  const [editTarget, setEditTarget] = useState<Assegnazione | null>(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Autocomplete dispositivo (solo per nuova)
  const [dispInput, setDispInput] = useState('');
  const [dispSugg, setDispSugg] = useState<DispositivoOption[]>([]);
  const [dispSel, setDispSel] = useState<DispositivoOption | null>(null);

  // Autocomplete dipendente
  const [dipInput, setDipInput] = useState('');
  const [dipSugg, setDipSugg] = useState<DipendenteOption[]>([]);
  const [dipSel, setDipSel] = useState<DipendenteOption | null>(null);

  // Autocomplete consegnato
  const [consInput, setConsInput] = useState('');
  const [consSugg, setConsSugg] = useState<DipendenteOption[]>([]);
  const [consSel, setConsSel] = useState<DipendenteOption | null>(null);

  // Campi form
  const [fDataAssegnazione, setFDataAssegnazione] = useState('');
  const [fNote, setFNote] = useState('');

  // Modal restituzione
  const [showRestituzione, setShowRestituzione] = useState(false);
  const [restTarget, setRestTarget] = useState<Assegnazione | null>(null);
  const [restData, setRestData] = useState('');
  const [restNote, setRestNote] = useState('');
  const [restSaving, setRestSaving] = useState(false);
  const [restError, setRestError] = useState('');

  // Modal riepilogo dipendente
  const [showRiepilogo, setShowRiepilogo] = useState(false);
  const [riepilogoLoading, setRiepilogoLoading] = useState(false);
  const [riepilogo, setRiepilogo] = useState<RiepilogoDipendente | null>(null);

  // Upload verbale
  const verbaleInputRef = useRef<HTMLInputElement>(null);
  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const [formVerbaleFile, setFormVerbaleFile] = useState<File | null>(null);
  const [verbaleUrlLoadingId, setVerbaleUrlLoadingId] = useState<number | null>(null);

  const dipDebRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const consDebRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dispDebRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Load assegnazioni ──────────────────────────────────────────────────────
  const loadAssegnazioni = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (societaId) p.set('societaId', societaId);
      if (fQ) p.set('q', fQ);
      if (fStato !== 'tutte') p.set('stato', fStato === 'attive' ? 'attive' : 'restituite');
      // @api GET /api/hr/assegnazioni-dispositivi?societaId&dipendenteId&stato&q -> {success:true,data:[{id,dispositivo,dipendente,consegnato,dataAssegnazione,dataRestituzione,noteRestituzione,note,verbaleFileName}]}
      const res = await apiFetch<{ success: boolean; data: Assegnazione[] }>(
        `/api/hr/assegnazioni-dispositivi?${p}`
      );
      setAssegnazioni(res.data);
    } catch (err: any) {
      showToast(err.message || 'Errore caricamento assegnazioni', 'error');
    } finally {
      setLoading(false);
    }
  }, [pronta, societaId, fQ, fStato]);

  useEffect(() => { loadAssegnazioni(); }, [loadAssegnazioni]);

  // ── Autocomplete dipendente/consegnato ─────────────────────────────────────
  const searchDip = useCallback(async (q: string, setSugg: (d: DipendenteOption[]) => void) => {
    if (!societaId) { setSugg([]); return; }
    try {
      const p = new URLSearchParams({ societaId });
      if (q) p.set('q', q);
      // @api GET /api/hr/assegnazioni-dispositivi/dipendenti-lista?societaId&q -> {success:true,data:[{id,nome,cognome,mansione,stato}]}
      const res = await apiFetch<{ success: boolean; data: DipendenteOption[] }>(
        `/api/hr/assegnazioni-dispositivi/dipendenti-lista?${p}`
      );
      setSugg(res.data.slice(0, 8));
    } catch { setSugg([]); }
  }, [societaId]);

  useEffect(() => {
    if (dipSel) return;
    if (dipDebRef.current) clearTimeout(dipDebRef.current);
    dipDebRef.current = setTimeout(() => searchDip(dipInput, setDipSugg), 300);
  }, [dipInput, dipSel, searchDip]);

  useEffect(() => {
    if (consSel) return;
    if (consDebRef.current) clearTimeout(consDebRef.current);
    consDebRef.current = setTimeout(() => searchDip(consInput, setConsSugg), 300);
  }, [consInput, consSel, searchDip]);

  // ── Autocomplete dispositivo (solo NON_ASSEGNATO) ──────────────────────────
  const searchDisp = useCallback(async (q: string) => {
    if (!societaId) { setDispSugg([]); return; }
    try {
      const p = new URLSearchParams({ stato: 'NON_ASSEGNATO' });
      if (societaId) p.set('societaId', societaId);
      if (q) p.set('q', q);
      // @api GET /api/hr/dispositivi-aziendali?societaId&stato&q -> {success:true,data:[{id,codiceMatricola,tipologia,marca,modello,stato}]}
      const res = await apiFetch<{ success: boolean; data: DispositivoOption[] }>(
        `/api/hr/dispositivi-aziendali?${p}`
      );
      setDispSugg(res.data.slice(0, 8));
    } catch { setDispSugg([]); }
  }, [societaId]);

  useEffect(() => {
    if (dispSel) return;
    if (dispDebRef.current) clearTimeout(dispDebRef.current);
    dispDebRef.current = setTimeout(() => searchDisp(dispInput), 300);
  }, [dispInput, dispSel, searchDisp]);

  // ── Handlers modal form ─────────────────────────────────────────────────────
  const openCreate = () => {
    setEditTarget(null);
    setDispSel(null); setDispInput(''); setDispSugg([]);
    setDipSel(null); setDipInput(''); setDipSugg([]);
    setConsSel(null); setConsInput(''); setConsSugg([]);
    setFDataAssegnazione(new Date().toISOString().substring(0, 10));
    setFNote('');
    setFormVerbaleFile(null);
    setFormError('');
    setShowModalForm(true);
  };

  const openEdit = (a: Assegnazione) => {
    setEditTarget(a);
    setDispSel(null); setDispInput(''); setDispSugg([]);
    setDipSel(null); setDipInput(''); setDipSugg([]);
    setConsSel(
      a.consegnato
        ? { id: a.consegnato.id, nome: a.consegnato.nome, cognome: a.consegnato.cognome, mansione: null }
        : null
    );
    setConsInput('');
    setConsSugg([]);
    setFDataAssegnazione(a.dataAssegnazione.substring(0, 10));
    setFNote(a.note || '');
    setFormError('');
    setShowModalForm(true);
  };

  const handleSave = async () => {
    if (!editTarget) {
      // Nuova
      if (!dispSel) { setFormError('Seleziona un dispositivo'); return; }
      if (!dipSel) { setFormError('Seleziona un dipendente'); return; }
    }
    if (!fDataAssegnazione) { setFormError('Data assegnazione obbligatoria'); return; }

    setSaving(true);
    setFormError('');
    try {
      if (editTarget) {
        const body = {
          dataAssegnazione: fDataAssegnazione,
          consegnatoId: consSel?.id || null,
          note: fNote || null,
        };
        // @api PUT /api/hr/assegnazioni-dispositivi/:id {dataAssegnazione,consegnatoId,note} -> {success:true,data:{...}}
        await apiFetch(`/api/hr/assegnazioni-dispositivi/${editTarget.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        showToast('Assegnazione modificata', 'success');
      } else {
        const body = {
          societaId: societaId ? parseInt(societaId, 10) : undefined,
          dispositivoId: dispSel!.id,
          dipendenteId: dipSel!.id,
          dataAssegnazione: fDataAssegnazione,
          consegnatoId: consSel?.id || null,
          note: fNote || null,
        };
        // @api POST /api/hr/assegnazioni-dispositivi {societaId,dispositivoId,dipendenteId,dataAssegnazione,consegnatoId,note} -> {success:true,data:{id}}
        const resCreate = await apiFetch<{ success: boolean; data: { id: number } }>('/api/hr/assegnazioni-dispositivi', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (formVerbaleFile) {
          const fd = new FormData();
          fd.append('verbale', formVerbaleFile);
          // @api POST /api/hr/assegnazioni-dispositivi/:id/verbale {verbale:file} -> {success:true,data:{verbaleGcsPath,verbaleFileName}}
          await apiFetch(`/api/hr/assegnazioni-dispositivi/${resCreate.data.id}/verbale`, {
            method: 'POST',
            body: fd,
          });
        }
        showToast('Assegnazione creata', 'success');
      }
      setShowModalForm(false);
      loadAssegnazioni();
    } catch (err: any) {
      setFormError(err.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // ── Upload verbale ─────────────────────────────────────────────────────────
  const handleVerbaleChange = async (e: React.ChangeEvent<HTMLInputElement>, assegnazioneId: number) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(assegnazioneId);
    try {
      const fd = new FormData();
      fd.append('verbale', file);
      // @api POST /api/hr/assegnazioni-dispositivi/:id/verbale {verbale:file} -> {success:true,data:{verbaleGcsPath,verbaleFileName}}
      await apiFetch(`/api/hr/assegnazioni-dispositivi/${assegnazioneId}/verbale`, {
        method: 'POST',
        body: fd,
      });
      showToast('Verbale caricato', 'success');
      loadAssegnazioni();
    } catch (err: any) {
      showToast(err.message || 'Errore upload verbale', 'error');
    } finally {
      setUploadingId(null);
      if (verbaleInputRef.current) verbaleInputRef.current.value = '';
    }
  };

  // ── Apri verbale (stream PDF via backend) ─────────────────────────────────
  const openVerbaleUrl = async (id: number) => {
    setVerbaleUrlLoadingId(id);
    try {
      // @api GET /api/hr/assegnazioni-dispositivi/:id/verbale -> [PDF stream]
      await apiOpenBlob(`/api/hr/assegnazioni-dispositivi/${id}/verbale`);
    } catch (err: any) {
      showToast(err.message || 'Errore apertura verbale', 'error');
    } finally {
      setVerbaleUrlLoadingId(null);
    }
  };

  // ── Modal restituzione ─────────────────────────────────────────────────────
  const openRestituzione = (a: Assegnazione) => {
    setRestTarget(a);
    setRestData(new Date().toISOString().substring(0, 10));
    setRestNote('');
    setRestError('');
    setShowRestituzione(true);
  };

  const handleRestituzione = async () => {
    if (!restData) { setRestError('Data restituzione obbligatoria'); return; }
    if (!restTarget) return;
    setRestSaving(true);
    setRestError('');
    try {
      // @api PATCH /api/hr/assegnazioni-dispositivi/:id/restituzione {dataRestituzione,noteRestituzione} -> {success:true,data:{dataRestituzione,noteRestituzione}}
      await apiFetch(`/api/hr/assegnazioni-dispositivi/${restTarget.id}/restituzione`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dataRestituzione: restData, noteRestituzione: restNote || null }),
      });
      showToast('Restituzione registrata', 'success');
      setShowRestituzione(false);
      loadAssegnazioni();
    } catch (err: any) {
      setRestError(err.message || 'Errore nella restituzione');
    } finally {
      setRestSaving(false);
    }
  };

  // ── Modal riepilogo dipendente ─────────────────────────────────────────────
  const openRiepilogo = async (dipId: number) => {
    setRiepilogo(null);
    setShowRiepilogo(true);
    setRiepilogoLoading(true);
    try {
      const p = new URLSearchParams();
      if (societaId) p.set('societaId', societaId);
      // @api GET /api/hr/assegnazioni-dispositivi/dipendente/:dipendenteId?societaId -> {success:true,data:{dipendente,assegnazioni}}
      const res = await apiFetch<{ success: boolean; data: RiepilogoDipendente }>(
        `/api/hr/assegnazioni-dispositivi/dipendente/${dipId}?${p}`
      );
      setRiepilogo(res.data);
    } catch (err: any) {
      showToast(err.message || 'Errore caricamento riepilogo', 'error');
      setShowRiepilogo(false);
    } finally {
      setRiepilogoLoading(false);
    }
  };

  // ── Autocomplete render helper ─────────────────────────────────────────────
  const renderAutocomplete = (
    label: string,
    required: boolean,
    sel: DipendenteOption | null,
    setSel: (d: DipendenteOption | null) => void,
    input: string,
    setInput: (v: string) => void,
    sugg: DipendenteOption[],
    setSugg: (d: DipendenteOption[]) => void,
    placeholder: string,
    onFocus?: () => void
  ) => (
    <div className="bop-form-group">
      <label className="bop-label">
        {label} {required && <span className="bop-text-danger">*</span>}
      </label>
      {sel ? (
        <span className="assegn__badge-sel bop-badge bop-badge--success">
          {sel.cognome} {sel.nome}
          <button className="assegn__badge-x" onClick={() => { setSel(null); setInput(''); setSugg([]); }}>✕</button>
        </span>
      ) : (
        <div className="assegn__autocomplete">
          <input
            className="bop-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={onFocus}
            placeholder={placeholder}
          />
          {sugg.length > 0 && (
            <ul className="assegn__suggestions">
              {sugg.map((s) => (
                <li
                  key={s.id}
                  className="assegn__suggestion-item"
                  onClick={() => { setSel(s); setInput(''); setSugg([]); }}
                >
                  {s.cognome} {s.nome}{s.mansione ? ` — ${s.mansione}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="assegn__page">

        {/* Header */}
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2 className="bop-page-title">Assegnazione Dispositivi</h2>
          </div>
          {canEdit && (
            <button className="bop-btn bop-btn-primary" onClick={openCreate} disabled={!pronta}>
              <PlusIcon /> Nuova Assegnazione
            </button>
          )}
        </div>

        {/* Filtri */}
        <div className="bop-filter-bar">
          {!isUnica && (
            <select
              className="bop-select assegn__filter-societa"
              value={societaId}
              onChange={(e) => setSocietaId(e.target.value)}
            >
              <option value="">Tutte le società</option>
              {listaSocieta.map((s) => (
                <option key={s.id} value={s.id}>{s.nome}</option>
              ))}
            </select>
          )}
          <input
            className="bop-input assegn__filter-q"
            placeholder="Cerca dipendente…"
            value={fQ}
            onChange={(e) => setFQ(e.target.value)}
          />
          <select
            className="bop-select assegn__filter-stato"
            value={fStato}
            onChange={(e) => setFStato(e.target.value)}
          >
            <option value="tutte">Tutte</option>
            <option value="attive">Solo attive</option>
            <option value="restituite">Solo restituite</option>
          </select>
        </div>

        {/* Tabella */}
        {!pronta ? (
          <div className="bop-card">
            <div className="bop-empty-state">
              <div className="bop-empty-state__icon"><SwapIcon /></div>
              <p className="bop-empty-state__text">Seleziona una società per visualizzare le assegnazioni</p>
            </div>
          </div>
        ) : (
          <div className="bop-card">
            <div className="bop-table-wrapper">
              {loading ? (
                <div className="inline-loading">Caricamento…</div>
              ) : assegnazioni.length === 0 ? (
                <div className="bop-empty-state">
                  <div className="bop-empty-state__icon"><SwapIcon /></div>
                  <h3 className="bop-empty-state__title">Nessuna assegnazione</h3>
                  <p className="bop-empty-state__text">Nessuna assegnazione trovata con i filtri selezionati</p>
                </div>
              ) : (
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>Dipendente</th>
                      <th>Dispositivo</th>
                      <th>Dal</th>
                      <th>Al</th>
                      <th>Consegnato da</th>
                      <th>Note</th>
                      <th>Verbale</th>
                      {canEdit && <th className="assegn__th-azioni">Azioni</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {assegnazioni.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <button
                            className="bop-btn bop-btn-ghost assegn__btn-dipendente"
                            onClick={() => openRiepilogo(a.dipendente.id)}
                          >
                            {a.dipendente.cognome} {a.dipendente.nome}
                          </button>
                        </td>
                        <td className="assegn__td-dispositivo">
                          <span className="assegn__matricola">{a.dispositivo.codiceMatricola}</span>
                          <span className="bop-text-muted"> — {a.dispositivo.tipologia.nome}</span>
                        </td>
                        <td>{fmtDate(a.dataAssegnazione)}</td>
                        <td>
                          {a.dataRestituzione ? (
                            fmtDate(a.dataRestituzione)
                          ) : (
                            <span className="bop-badge bop-badge--success">Attiva</span>
                          )}
                        </td>
                        <td className="bop-text-muted">
                          {a.consegnato
                            ? `${a.consegnato.cognome} ${a.consegnato.nome}`
                            : '—'}
                        </td>
                        <td className="bop-text-muted assegn__td-note">
                          {a.note || '—'}
                        </td>
                        <td>
                          {a.verbaleFileName ? (
                            <button
                              className="assegn__link-verbale"
                              onClick={() => openVerbaleUrl(a.id)}
                              disabled={verbaleUrlLoadingId === a.id}
                              title={a.verbaleFileName}
                            >
                              <LinkIcon /> {verbaleUrlLoadingId === a.id ? '…' : 'Verbale'}
                            </button>
                          ) : canEdit ? (
                            <>
                              <input
                                ref={uploadingId === a.id ? verbaleInputRef : undefined}
                                type="file"
                                accept=".pdf"
                                className="assegn__file-input-hidden"
                                id={`verbale-${a.id}`}
                                onChange={(e) => handleVerbaleChange(e, a.id)}
                              />
                              <label
                                htmlFor={`verbale-${a.id}`}
                                className="assegn__btn-upload"
                                title="Carica verbale PDF"
                              >
                                {uploadingId === a.id ? (
                                  <span className="bop-text-muted">…</span>
                                ) : (
                                  <><UploadIcon /> Carica</>
                                )}
                              </label>
                            </>
                          ) : (
                            <span className="bop-text-muted">—</span>
                          )}
                        </td>
                        {canEdit && (
                          <td>
                            <div className="assegn__actions">
                              <button
                                className="bop-btn-icon assegn__btn-edit"
                                title="Modifica"
                                onClick={() => openEdit(a)}
                              >
                                <EditIcon />
                              </button>
                              {!a.dataRestituzione && (
                                <button
                                  className="bop-btn bop-btn-ghost assegn__btn-restituzione"
                                  onClick={() => openRestituzione(a)}
                                >
                                  Restituzione
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ── Modale Nuova/Modifica Assegnazione ──────────────────────────── */}
        {showModalForm && (
          <div className="bop-modal-overlay">
            <div className="bop-modal assegn__modal-form">
              <div className="bop-modal__header">
                <h3 className="bop-modal__title">
                  {editTarget ? 'Modifica Assegnazione' : 'Nuova Assegnazione'}
                </h3>
                <button className="bop-btn-icon" onClick={() => setShowModalForm(false)}>
                  <XIcon />
                </button>
              </div>
              <div className="bop-modal__body">
                {formError && <div className="assegn__modal-error">{formError}</div>}

                {/* Dispositivo — solo per nuova */}
                {!editTarget && (
                  <div className="bop-form-group">
                    <label className="bop-label">Dispositivo <span className="bop-text-danger">*</span></label>
                    {dispSel ? (
                      <div className="assegn__disp-sel">
                        <div className="assegn__disp-sel__main">
                          <strong>{dispSel.codiceMatricola}</strong>
                          <span className="bop-text-muted"> — {dispSel.tipologia.nome}</span>
                          <button
                            className="assegn__badge-x"
                            onClick={() => { setDispSel(null); setDispInput(''); setDispSugg([]); }}
                          >✕</button>
                        </div>
                        {(dispSel.marca || dispSel.modello) && (
                          <div className="assegn__disp-sel__sub bop-text-muted">
                            {[dispSel.marca, dispSel.modello].filter(Boolean).join(' ')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="assegn__autocomplete">
                        <input
                          className="bop-input"
                          value={dispInput}
                          onChange={(e) => setDispInput(e.target.value)}
                          onFocus={() => { if (!dispSel) searchDisp(dispInput); }}
                          placeholder="Cerca per matricola, marca, modello…"
                        />
                        {dispSugg.length > 0 && (
                          <ul className="assegn__suggestions">
                            {dispSugg.map((d) => (
                              <li
                                key={d.id}
                                className="assegn__suggestion-item"
                                onClick={() => { setDispSel(d); setDispInput(''); setDispSugg([]); }}
                              >
                                <div><strong>{d.codiceMatricola}</strong> — {d.tipologia.nome}</div>
                                {(d.marca || d.modello) && (
                                  <div className="assegn__suggestion-item__sub">
                                    {[d.marca, d.modello].filter(Boolean).join(' ')}
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Dipendente — solo per nuova */}
                {!editTarget && renderAutocomplete(
                  'Dipendente', true, dipSel, setDipSel,
                  dipInput, setDipInput, dipSugg, setDipSugg,
                  'Cerca dipendente…',
                  () => { if (!dipSel) searchDip(dipInput, setDipSugg); }
                )}

                {/* Consegnato da — sempre */}
                {renderAutocomplete(
                  'Consegnato da', false, consSel, setConsSel,
                  consInput, setConsInput, consSugg, setConsSugg,
                  'Cerca dipendente (opzionale)…',
                  () => { if (!consSel) searchDip(consInput, setConsSugg); }
                )}

                <div className="bop-form-group">
                  <label className="bop-label">Data assegnazione <span className="bop-text-danger">*</span></label>
                  <input
                    type="date"
                    className="bop-input assegn__date-input"
                    value={fDataAssegnazione}
                    onChange={(e) => setFDataAssegnazione(e.target.value)}
                  />
                </div>

                <div className="bop-form-group">
                  <label className="bop-label">Note</label>
                  <textarea
                    className="bop-input assegn__textarea"
                    value={fNote}
                    onChange={(e) => setFNote(e.target.value)}
                    rows={3}
                    placeholder="Note sull'assegnazione…"
                  />
                </div>

                {/* Verbale — solo per nuova assegnazione */}
                {!editTarget && (
                  <div className="bop-form-group">
                    <label className="bop-label">Verbale di consegna (PDF)</label>
                    <input
                      type="file"
                      accept=".pdf"
                      className="bop-input"
                      onChange={(e) => setFormVerbaleFile(e.target.files?.[0] || null)}
                    />
                    {formVerbaleFile && (
                      <span className="bop-text-muted assegn__verbale-filename">
                        {formVerbaleFile.name}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={() => setShowModalForm(false)} disabled={saving}>
                  Annulla
                </button>
                <button className="bop-btn bop-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Salvataggio…' : 'Salva'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Mini-modale Registra Restituzione ───────────────────────────── */}
        {showRestituzione && restTarget && (
          <div className="bop-modal-overlay">
            <div className="bop-modal assegn__modal-rest">
              <div className="bop-modal__header">
                <h3 className="bop-modal__title">Registra restituzione</h3>
                <button className="bop-btn-icon" onClick={() => setShowRestituzione(false)}>
                  <XIcon />
                </button>
              </div>
              <div className="bop-modal__body">
                {restError && <div className="assegn__modal-error">{restError}</div>}
                <div className="assegn__rest-info">
                  <span className="bop-text-muted">Dipendente: </span>
                  <strong>{restTarget.dipendente.cognome} {restTarget.dipendente.nome}</strong>
                  <span className="bop-text-muted"> · </span>
                  <span className="bop-text-muted">Dispositivo: </span>
                  <strong>{restTarget.dispositivo.codiceMatricola}</strong>
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Data restituzione <span className="bop-text-danger">*</span></label>
                  <input
                    type="date"
                    className="bop-input assegn__date-input"
                    value={restData}
                    onChange={(e) => setRestData(e.target.value)}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Note restituzione</label>
                  <textarea
                    className="bop-input assegn__textarea"
                    value={restNote}
                    onChange={(e) => setRestNote(e.target.value)}
                    rows={3}
                    placeholder="Condizioni di restituzione, anomalie…"
                  />
                </div>
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={() => setShowRestituzione(false)} disabled={restSaving}>
                  Annulla
                </button>
                <button className="bop-btn bop-btn-primary" onClick={handleRestituzione} disabled={restSaving}>
                  {restSaving ? 'Registrazione…' : 'Conferma restituzione'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modale Riepilogo Dispositivi Dipendente ──────────────────────── */}
        {showRiepilogo && (
          <div className="bop-modal-overlay">
            <div className="bop-modal assegn__modal-riepilogo">
              <div className="bop-modal__header">
                <h3 className="bop-modal__title">
                  Dispositivi —{' '}
                  {riepilogo
                    ? `${riepilogo.dipendente.cognome} ${riepilogo.dipendente.nome}`
                    : '…'}
                </h3>
                <button className="bop-btn-icon" onClick={() => setShowRiepilogo(false)}>
                  <XIcon />
                </button>
              </div>
              <div className="bop-modal__body">
                {riepilogoLoading ? (
                  <div className="inline-loading">Caricamento…</div>
                ) : !riepilogo || riepilogo.assegnazioni.length === 0 ? (
                  <div className="bop-empty-state">
                    <p className="bop-empty-state__text">Nessun dispositivo assegnato a questo dipendente</p>
                  </div>
                ) : (
                  <div className="bop-table-wrapper">
                    <table className="bop-table">
                      <thead>
                        <tr>
                          <th>Dispositivo</th>
                          <th>Tipologia</th>
                          <th>Dal</th>
                          <th>Al</th>
                          <th>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {riepilogo.assegnazioni.map((a) => (
                          <tr key={a.id}>
                            <td className="assegn__matricola">{a.dispositivo.codiceMatricola}</td>
                            <td>{a.dispositivo.tipologia.nome}</td>
                            <td>{fmtDate(a.dataAssegnazione)}</td>
                            <td>
                              {a.dataRestituzione ? (
                                fmtDate(a.dataRestituzione)
                              ) : (
                                <span className="bop-badge bop-badge--success">Attiva</span>
                              )}
                            </td>
                            <td className="bop-text-muted">{a.note || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={() => setShowRiepilogo(false)}>
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
